import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Buildings, Key, Rocket, CheckCircle, ArrowLeft } from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  { value: 'textiles_apparel', label: 'Textiles & Apparel' },
  { value: 'jewelry_accessories', label: 'Jewelry & Accessories' },
  { value: 'leather_bags', label: 'Leather & Bags' },
  { value: 'home_furniture', label: 'Home & Furniture' },
  { value: 'general', label: 'General Products' }
];

const CompanyRegisterPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website: '',
    industry: 'general'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/companies/register?name=${encodeURIComponent(formData.name)}&email=${encodeURIComponent(formData.email)}&industry=${formData.industry}&website=${encodeURIComponent(formData.website || '')}`
      );
      
      if (response.data.success) {
        setCompanyData(response.data);
        setStep(2);
        // Store company ID
        localStorage.setItem('ginsgood_company_id', response.data.company_id);
        localStorage.setItem('ginsgood_company_name', formData.name);
        toast.success('Company registered successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>

          <div className="text-center mb-8">
            <Badge variant="outline" className="mb-4">
              <Buildings weight="duotone" className="w-4 h-4 mr-2" />
              B2B Platform
            </Badge>
            <h1 className="text-3xl md:text-4xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Register Your Company
            </h1>
            <p className="text-muted-foreground">
              Join GinsGood to certify your products with industry-standard sustainability scores
            </p>
          </div>

          {step === 1 && (
            <Card className="p-8" data-testid="company-register-form">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label>Company Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your Company Name"
                    required
                    data-testid="company-name-input"
                  />
                </div>

                <div>
                  <Label>Business Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="contact@company.com"
                    required
                    data-testid="company-email-input"
                  />
                </div>

                <div>
                  <Label>Website (optional)</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://yourcompany.com"
                  />
                </div>

                <div>
                  <Label>Primary Industry *</Label>
                  <Select value={formData.industry} onValueChange={(val) => setFormData({ ...formData, industry: val })}>
                    <SelectTrigger data-testid="industry-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map(ind => (
                        <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Pricing Info */}
                <div className="bg-secondary/50 rounded-xl p-4 space-y-3">
                  <h3 className="font-medium">Starter Plan (Free Trial)</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>✓ 100 certificates/month</li>
                    <li>✓ Basic API access</li>
                    <li>✓ Email support</li>
                    <li>✓ All industry templates</li>
                  </ul>
                </div>

                <Button type="submit" className="w-full h-12 rounded-full gap-2" disabled={loading} data-testid="register-company-btn">
                  <Rocket weight="bold" className="w-5 h-5" />
                  {loading ? 'Registering...' : 'Register Company'}
                </Button>
              </form>
            </Card>
          )}

          {step === 2 && companyData && (
            <Card className="p-8 text-center" data-testid="registration-success">
              <div className="w-20 h-20 mx-auto bg-[#4A6C4C]/10 rounded-full flex items-center justify-center mb-6">
                <CheckCircle weight="duotone" className="w-10 h-10 text-[#4A6C4C]" />
              </div>
              
              <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Welcome to GinsGood!
              </h2>
              <p className="text-muted-foreground mb-6">Your company has been registered. Save your API keys securely.</p>

              <div className="bg-secondary/50 rounded-xl p-6 text-left mb-6 space-y-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Company ID</Label>
                  <p className="font-mono text-sm break-all">{companyData.company_id}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground flex items-center gap-2">
                    <Key weight="duotone" className="w-4 h-4" /> Live API Key
                  </Label>
                  <p className="font-mono text-sm break-all bg-[#1a1a1a] text-[#4ADE80] p-2 rounded">{companyData.api_key_live}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground flex items-center gap-2">
                    <Key weight="duotone" className="w-4 h-4" /> Test API Key
                  </Label>
                  <p className="font-mono text-sm break-all bg-[#1a1a1a] text-[#FCD34D] p-2 rounded">{companyData.api_key_test}</p>
                </div>
              </div>

              <div className="bg-[#C26D53]/10 border border-[#C26D53]/30 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-[#C26D53] font-medium">⚠️ Important: Save these API keys now!</p>
                <p className="text-sm text-muted-foreground">They won't be shown again. Use test key for development, live key for production.</p>
              </div>

              <div className="flex flex-col gap-3">
                <Button onClick={() => navigate(`/company/dashboard/${companyData.company_id}`)} className="w-full gap-2">
                  Go to Dashboard
                </Button>
                <Button variant="outline" onClick={() => navigate('/api-docs')} className="w-full">
                  View API Documentation
                </Button>
              </div>
            </Card>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CompanyRegisterPage;
